/**
 * RichSelect — searchable single/multi select; Command + Popover wrapper
 *
 * @example
 * // Single
 * <RichSelect
 *   options={courses.map(c => ({ value: c.id, label: c.title }))}
 *   value={selectedCourse}
 *   onChange={setSelectedCourse}
 *   placeholder={t('admin.select_course')}
 * />
 *
 * // Multi
 * <RichSelect
 *   multiple
 *   options={users.map(u => ({ value: u.id, label: u.name, sublabel: u.email }))}
 *   value={selectedUsers}
 *   onChange={setSelectedUsers}
 *   placeholder={t('admin.select_users')}
 * />
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

// ─── RichSelect ───────────────────────────────────────────────────────────────
export default function RichSelect({
  options,
  value,
  onChange,
  placeholder,
  multiple,
  searchPlaceholder,
  disabled,
  className,
  maxDisplay,
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const getLabel = (val) => options.find((o) => o.value === val)?.label;

  const isSelected = (val) => {
    if (multiple) return Array.isArray(value) && value.includes(val);
    return value === val;
  };

  const toggle = (val) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      const next = current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val];
      onChange?.(next);
    } else {
      onChange?.(value === val ? null : val);
      setOpen(false);
    }
  };

  const removeTag = (val, e) => {
    e.stopPropagation();
    if (multiple) {
      onChange?.((value || []).filter((v) => v !== val));
    } else {
      onChange?.(null);
    }
  };

  const displayValues = multiple ? (value || []) : (value ? [value] : []);
  const displayCount = maxDisplay || 3;
  const extraCount = displayValues.length - displayCount;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            'w-full justify-between rounded-xl border-2 border-[var(--border-light)] h-auto min-h-9 py-1.5 px-3 font-normal bg-white',
            'hover:border-[var(--brand-primary)] focus:border-[var(--brand-primary)]',
            className,
          )}
        >
          <div className="flex flex-wrap gap-1.5 flex-1 text-left">
            {displayValues.length === 0 ? (
              <span className="text-[var(--text-muted)] text-sm">{placeholder || t('admin.select_placeholder')}</span>
            ) : (
              <>
                {displayValues.slice(0, displayCount).map((val) => (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--brand-primary)]/10 border border-[var(--brand-primary)]/20 text-[var(--brand-primary)] text-xs font-bold"
                  >
                    {getLabel(val)}
                    {(multiple || !multiple) && (
                      <button
                        onClick={(e) => removeTag(val, e)}
                        className="hover:opacity-70"
                        aria-label={`Remove ${getLabel(val)}`}
                      >
                        <X size={10} />
                      </button>
                    )}
                  </span>
                ))}
                {extraCount > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--bg-app)] border text-xs font-bold text-[var(--text-muted)]">
                    +{extraCount}
                  </span>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown size={14} className="ml-2 flex-shrink-0 text-[var(--text-muted)]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 rounded-xl border-2 border-[var(--border-light)] shadow-[0_4px_0_0_var(--border-light)]"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder || t('admin.rich_select_search')}
            className="h-9 text-sm"
          />
          <CommandList>
            <CommandEmpty className="py-6 text-sm text-center text-[var(--text-muted)]">
              {t('admin.rich_select_no_results')}
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const selected = isSelected(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    onSelect={() => toggle(opt.value)}
                    disabled={opt.disabled}
                    className="rounded-lg cursor-pointer gap-2 py-2"
                  >
                    <div
                      className={cn(
                        'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                        selected
                          ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)]'
                          : 'border-[var(--border-heavy)]',
                      )}
                    >
                      {selected && <Check size={10} className="text-white" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{opt.label}</p>
                      {opt.sublabel && (
                        <p className="text-xs text-[var(--text-muted)] truncate">{opt.sublabel}</p>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

RichSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sublabel: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  /** Single: string | Multi: string[] */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  /** Max chips to show before "+N" */
  maxDisplay: PropTypes.number,
};

RichSelect.defaultProps = {
  multiple: false,
  disabled: false,
  maxDisplay: 3,
};
